import {
  FastField,
  Field,
  FieldInputProps,
  FieldMetaProps,
  FieldProps
} from "formik";
import _ from "lodash";
import { ChangeEvent, ChangeEventHandler, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  makeStyles,
  MenuItem,
  FormHelperText,
  TextFieldProps,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  FormControl,
  InputLabel
} from "@material-ui/core";

import palette from "src/styles/palette";

import FormTextField from "./FormTextField";

const useSelectStyles = makeStyles(() => ({
  noneLabel: {
    color: palette.darkGray
  }
}));

type BaseSelectProps = Pick<
  TextFieldProps,
  | "children"
  | "className"
  | "disabled"
  | "fullWidth"
  | "label"
  | "name"
  | "onBlur"
  | "error"
  | "placeholder"
  | "InputProps"
  | "helperText"
> &
  Pick<MuiSelectProps, "MenuProps" | "renderValue"> & {
    variant?: "outlined" | "underlined";
  };

export type SelectProps = BaseSelectProps & {
  withNoneOption?: boolean;
  noneOptionLabel?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<{ value: string }>;
};

type MultiSelectProps = BaseSelectProps & {
  value?: string[];
  onChange?: ChangeEventHandler<{ value: string[] }>;
};

export default function Select({
  withNoneOption,
  noneOptionLabel = "None",
  value,
  onChange,
  children,
  MenuProps,
  renderValue,
  className,
  disabled,
  fullWidth,
  label,
  name,
  onBlur,
  error,
  variant = "outlined",
  ...textFieldProps
}: SelectProps): JSX.Element {
  const classes = useSelectStyles();

  const id = useMemo(() => uuidv4(), []);

  switch (variant) {
    case "outlined":
      return (
        // @ts-ignore disableUnderline isn't technically allowed if you pass variant="outlined"
        <FormTextField
          select
          value={value}
          SelectProps={{
            displayEmpty: true,
            MenuProps,
            renderValue,
            onChange: event =>
              onChange && onChange(event as ChangeEvent<{ value: string }>)
          }}
          className={className}
          disabled={disabled}
          fullWidth={fullWidth}
          label={label}
          name={name}
          onBlur={onBlur}
          error={error}
          {...textFieldProps}
        >
          {withNoneOption ? (
            <MenuItem key="NONE" value="">
              {/* TODO: MUI Typography? */}
              <span className={classes.noneLabel}>{noneOptionLabel}</span>
            </MenuItem>
          ) : null}
          {children}
        </FormTextField>
      );
    case "underlined":
      return (
        <FormControl className={className}>
          {label ? (
            <InputLabel shrink id={id}>
              {label}
            </InputLabel>
          ) : null}
          <MuiSelect
            labelId={id}
            value={value}
            onChange={event =>
              onChange && onChange(event as ChangeEvent<{ value: string }>)
            }
            disabled={disabled}
            fullWidth={fullWidth}
            name={name}
            onBlur={onBlur}
            error={error}
            MenuProps={MenuProps}
            renderValue={renderValue}
            {...textFieldProps}
          >
            {children}
          </MuiSelect>
        </FormControl>
      );
  }
}

export function MultiSelect({
  value,
  onChange,
  MenuProps,
  children,
  renderValue,
  variant = "outlined",
  className,
  disabled,
  fullWidth,
  label,
  name,
  onBlur,
  error,
  ...textFieldProps
}: MultiSelectProps): JSX.Element {
  const id = useMemo(() => uuidv4(), []);

  switch (variant) {
    case "outlined":
      return (
        // @ts-ignore disableUnderline isn't technically allowed if you pass variant="outlined"
        <FormTextField
          select
          name={name}
          label={label}
          value={value}
          // @ts-ignore
          onChange={onChange}
          onBlur={onBlur}
          error
          SelectProps={{
            displayEmpty: true,
            multiple: true,
            MenuProps,
            renderValue
          }}
          className={className}
          disabled={disabled}
          fullWidth={fullWidth}
          {...textFieldProps}
        >
          {children}
        </FormTextField>
      );
    case "underlined":
      return (
        <FormControl className={className}>
          <InputLabel shrink id={id}>
            {label}
          </InputLabel>
          <MuiSelect
            labelId={id}
            value={value}
            // @ts-ignore
            onChange={onChange}
            disabled={disabled}
            fullWidth={fullWidth}
            name={name}
            onBlur={onBlur}
            error={error}
            MenuProps={MenuProps}
            renderValue={renderValue}
            multiple
          >
            {children}
          </MuiSelect>
        </FormControl>
      );
  }
}

export function FormikSelect({
  name,
  fast,
  overrideFormikProps,
  ...selectProps
}: Omit<SelectProps, "value"> & {
  name: string;
  fast?: boolean;
  onChange?: ChangeEventHandler<{ value: string[] | string }>;
  overrideFormikProps?: (
    field: FieldInputProps<unknown>,
    meta: FieldMetaProps<unknown>
  ) => FieldInputProps<unknown>;
}): JSX.Element {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name}>
      {({ field: { multiple, ...formikField }, meta }: FieldProps) => {
        const formikProps = overrideFormikProps
          ? overrideFormikProps(formikField, meta)
          : formikField;
        const error = meta.touched ? meta.error : null;
        return (
          <>
            {multiple ? (
              <MultiSelect {...formikProps} {...selectProps} error={!!error} />
            ) : (
              <Select {...formikProps} {...selectProps} error={!!error} />
            )}
            {error ? <FormHelperText error>{error}</FormHelperText> : null}
          </>
        );
      }}
    </Component>
  );
}
